import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { SuggestInput } from "../components/SuggestInput";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface View {
  emoji: string;
  total: number;
  guesses: string[];
  year?: number;
  answer?: { uk: string; en: string; y: number };
}

const COPY = {
  unknown_film: "Не знаю такого фільму — вибери з підказок",
  already_guessed: "Вже було",
};

/** Emoji are multi-codepoint — segment before counting. */
function parts(e: string): string[] {
  return [...new Intl.Segmenter("uk", { granularity: "grapheme" }).segment(e)].map((x) => x.segment);
}

export function Emovi({ date }: { date?: string | undefined }) {
  const g = useGame<View>("emovi", COPY, date);
  const [names, setNames] = useState<{ uk: string; en: string }[]>([]);

  useEffect(() => {
    void api.names("emovi").then((r) => setNames(r.names)).catch(() => {});
  }, []);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const shown = parts(v.emoji);
  const hidden = Math.max(0, v.total - shown.length);
  const wrongs = won ? v.guesses.slice(0, -1) : v.guesses;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ЕМОВІ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mb-3 text-center text-xs text-tghint">вгадай фільм — кожен промах відкриває ще одне емодзі</div>
        <div className="mx-auto flex max-w-[420px] flex-wrap items-center justify-center gap-2 rounded-2xl bg-tgsection px-4 py-6 text-[34px] leading-none">
          {shown.map((e, i) => (
            <span key={i}>{e}</span>
          ))}
          {Array.from({ length: hidden }, (_, i) => (
            <span key={`h${i}`} className="opacity-30">⬛</span>
          ))}
        </div>
        {v.year !== undefined && !finished && (
          <div className="mt-2 text-center text-sm text-tghint">💡 рік: {v.year}</div>
        )}
        {wrongs.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {[...wrongs].reverse().map((t) => (
              <li key={t} className="rounded-xl bg-tgsection px-3 py-2 text-sm">
                ❌ {t}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        <SuggestInput
          names={names}
          disabled={finished}
          busy={g.busy}
          exclude={guessed}
          onSubmit={(name) => void g.run(() => api.guess<View>("emovi", name, date))}
          placeholder="назва фільму"
        />
        {!finished && v.guesses.length >= 3 && (
          <div className="mt-2 flex px-4">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("emovi", date))} />
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Кіноман!" : "Не сьогодні"}
          subtitle={won ? `за ${v.guesses.length} · стрік ${s.me.streak} 🔥` : "стрік згорів"}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 rounded-xl bg-[rgba(128,128,128,0.12)] px-4 py-3 text-center">
              <div className="text-2xl">{v.emoji}</div>
              <div className="mt-1 text-lg font-bold">{v.answer.uk}</div>
              <div className="text-xs text-tghint">
                {v.answer.en} · {v.answer.y}
              </div>
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
